import React, { useEffect, useState, useRef } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
const faqs = [{
  question: 'How fast can I actually see results?',
  answer: 'Most store owners see their first recovered sale within 24-48 hours of installation. The floating cart and countdown timers start working immediately. Our average customer reports measurable conversion improvements within the first week. The free plan lets you prove this to yourself with zero risk.'
}, {
  question: "Will this slow down my store? I can't afford slower load times.",
  answer: "Actually, it's the opposite. We're obsessed with performance—our code is benchmarked and optimized to add less than 0.1 seconds to load time. Many customers report FASTER checkout times because of our streamlined cart. We load modules asynchronously and only when needed. Your speed scores will thank you."
}, {
  question: "What if it doesn't work with my theme or other plugins?",
  answer: "CommerceBoost works with 99.8% of WooCommerce themes and plugins out of the box. We test against the top 100 themes monthly. On the rare occasion there's a conflict, our support team (not outsourced, real developers) will personally fix it for Pro users—usually within 24 hours. That's part of your subscription."
}, {
  question: "Is there really a money-back guarantee? What's the catch?",
  answer: "Zero catch. Use CommerceBoost for 30 days. If you don't see increased conversions or you're unhappy for ANY reason, email support@commerceboost.io and we refund you immediately—no questions, no hoops. We can offer this because less than 2% of customers ask for refunds. It works, plain and simple."
}, {
  question: "I'm not technical. Can I actually set this up myself?",
  answer: 'If you can install a WordPress plugin (literally click "Upload" then "Activate"), you can set this up. Our dashboard uses simple toggles and dropdowns—no coding required. We include video tutorials for every feature. And Pro users get white-glove installation support if you want us to do it for you.'
}, {
  question: 'How many sites can I use this on?',
  answer: 'The Free plan works on one site forever. The Professional plan ($79/year) covers one site with full features. The Business plan ($149/year) covers 5 sites. The Enterprise plan ($199/year) gives you UNLIMITED installations—perfect for agencies managing multiple client stores. All plans include updates and support.'
}];
export function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const faqRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const toggleFAQ = index => {
    setOpenIndex(openIndex === index ? null : index);
  };
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.disconnect();
      }
    }, {
      threshold: 0.1
    });
    if (faqRef.current) {
      observer.observe(faqRef.current);
    }
    return () => {
      if (faqRef.current) {
        observer.disconnect();
      }
    };
  }, []);
  return <section id="faq" className="py-20 md:py-32 bg-gray-50" ref={faqRef} data-id="element-52">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl" data-id="element-53">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-54">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight" data-id="element-55">
            Your Questions,{' '}
            <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent" data-id="element-56">
              Answered Honestly
            </span>
          </h2>
          <p className="text-xl text-gray-600" data-id="element-57">
            We know you're skeptical (you should be). Here are the real answers
            to what everyone asks before buying.
          </p>
        </div>

        <div className="max-w-3xl mx-auto" data-id="element-58">
          {faqs.map((faq, index) => <div key={index} className={`bg-white rounded-xl mb-4 border border-gray-200 transition-all duration-500 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{
          transitionDelay: `${index * 100}ms`
        }} data-id="element-59">
              <button className="flex justify-between items-center w-full p-6 text-left" onClick={() => toggleFAQ(index)} aria-expanded={openIndex === index} data-id="element-60">
                <h3 className="text-lg font-semibold text-gray-900 pr-8" data-id="element-61">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0" data-id="element-62">
                  {openIndex === index ? <ChevronUp className="h-5 w-5 text-purple-600" data-id="element-63" /> : <ChevronDown className="h-5 w-5 text-gray-500" data-id="element-64" />}
                </div>
              </button>
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'}`} data-id="element-65">
                <p className="px-6 pb-6 text-gray-600 leading-relaxed" data-id="element-66">
                  {faq.answer}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-16 text-center max-w-3xl mx-auto bg-white rounded-2xl p-8 border border-gray-200" data-id="element-67">
          <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-68">
            Still not convinced? Talk to a human.
          </h3>
          <p className="text-gray-600 mb-6" data-id="element-69">
            Our founder personally answers emails. Ask the tough questions.
            We'll give you straight answers—not sales pitches.
          </p>
          <a href="mailto:support@commerceboost.io" className="inline-flex items-center justify-center px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500 text-white font-medium rounded-lg transition-all shadow-lg shadow-purple-500/25" data-id="element-70">
            Email Us Your Questions
          </a>
        </div>
      </div>
    </section>;
}